import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, CreditCard, Home as HomeIcon, ShieldCheck, ArrowRight, Upload, MessageSquare, FileSearch, Mic, Globe, Lock } from "lucide-react";
import { PublicNav } from "@/components/PublicNav";
import { AskField } from "@/components/AskField";
import { TrustBar } from "@/components/TrustBar";
import { LanguagePill } from "@/components/LanguagePill";
import { useI18n } from "@/i18n";

export default function Landing() {
  const { t } = useI18n();
  const location = useLocation();

  useEffect(() => {
    const target = location.state?.scrollTo || (location.hash ? location.hash.slice(1) : null);
    if (target) {
      setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location]);

  const suggestions = [
    { icon: CreditCard, text: t("q1"), color: "text-blue-500 bg-blue-50" },
    { icon: HomeIcon, text: t("q2"), color: "text-teal-500 bg-teal-50" },
    { icon: ShieldCheck, text: t("q3"), color: "text-indigo-500 bg-indigo-50" },
  ];

  const features = [
    { icon: FileSearch, titleKey: "feat_glance_t", descKey: "feat_glance_d" },
    { icon: MessageSquare, titleKey: "feat_ask_t", descKey: "feat_ask_d" },
    { icon: Mic, titleKey: "feat_talk_t", descKey: "feat_talk_d" },
    { icon: ShieldCheck, titleKey: "feat_score_t", descKey: "feat_score_d" },
    { icon: Upload, titleKey: "feat_wallet_t", descKey: "feat_wallet_d" },
    { icon: Globe, titleKey: "feat_state_t", descKey: "feat_state_d" },
  ];

  return (
    <div className="min-h-screen hero-bg">
      <PublicNav />

      <section className="relative overflow-hidden">
        {/* watermark shapes */}
        <HomeIcon className="pointer-events-none absolute left-6 bottom-10 h-40 w-40 text-blue-900/5 hidden md:block" strokeWidth={1} />
        <ShieldCheck className="pointer-events-none absolute right-10 top-40 h-36 w-36 text-teal-900/5 hidden md:block" strokeWidth={1} />

        <div className="mx-auto max-w-5xl px-5 pt-10 pb-6">
          <div className="flex justify-end mb-4"><LanguagePill /></div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 border border-teal-100 px-4 py-1.5 text-sm font-medium text-brandteal">
              <Sparkles className="h-4 w-4" /> {t("ai_badge")}
            </span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-navy leading-[1.05]">
              {t("hero_head_1")} <span className="brand-gradient-text">{t("hero_head_accent")}</span>
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-slate-500">{t("hero_sub")}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-9 mx-auto max-w-3xl">
            <AskField />
          </motion.div>

          <div className="mt-12">
            <p className="text-center font-display text-xl text-navy mb-4">{t("try_asking")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {suggestions.map((s, i) => (
                <Link key={i} to="/register" data-testid={`suggestion-${i}`}
                  className="group flex items-center gap-3 rounded-2xl bg-white border border-slate-100 card-shadow px-4 py-4 hover:-translate-y-0.5 transition-transform">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.color}`}><s.icon className="h-5 w-5" /></span>
                  <span className="flex-1 text-sm font-medium text-navy">{s.text}</span>
                  <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-brandblue transition-colors" />
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-12"><TrustBar /></div>

          <p className="mt-8 text-center text-sm text-slate-400 flex items-center justify-center gap-2">
            <span>🇺🇸</span> {t("footer_note")}
          </p>
        </div>
      </section>

      <section id="how" className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-5">
          <div className="text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-navy">{t("how_head")}</h2>
            <p className="mt-3 text-slate-500 max-w-xl mx-auto">{t("how_sub")}</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: t("how_1_t"), d: t("how_1_d") },
              { n: "02", t: t("how_2_t"), d: t("how_2_d") },
              { n: "03", t: t("how_3_t"), d: t("how_3_d") },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-slate-100 card-shadow p-7">
                <span className="font-display text-3xl brand-gradient-text">{s.n}</span>
                <h3 className="mt-3 text-xl font-semibold text-navy">{s.t}</h3>
                <p className="mt-2 text-slate-500">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-20 hero-bg">
        <div className="mx-auto max-w-6xl px-5">
          <div className="text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-navy">{t("features_head")}</h2>
            <p className="mt-3 text-slate-500 max-w-xl mx-auto">{t("features_sub")}</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.titleKey} className="rounded-2xl bg-white border border-slate-100 card-shadow p-7 hover:-translate-y-1 transition-transform">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl brand-gradient text-white"><f.icon className="h-5 w-5" /></span>
                <h3 className="mt-4 text-lg font-semibold text-navy">{t(f.titleKey)}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t(f.descKey)}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/register" data-testid="cta-getstarted"
              className="inline-flex items-center gap-2 rounded-full brand-gradient px-7 py-3.5 text-white font-semibold card-shadow hover:opacity-90">
              {t("get_started")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100 bg-white py-8">
        <div className="mx-auto max-w-6xl px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400">{t("footer_copy")}</p>
          <div className="flex gap-6 text-sm text-slate-500">
            <Link to="/trust" className="hover:text-brandblue">{t("trust_center")}</Link>
            <a href="#features" className="hover:text-brandblue">{t("nav_features_lbl")}</a>
            <Link to="/admin" data-testid="footer-admin-link" className="hover:text-brandblue">{t("admin_login")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
